import React from 'react';
import './FuelsFinancialSummaryPanel.css';

/**
 * Bunkers Financial Summary (Phase 1, May 2026).
 *
 * Read-only KPI block on the Fuels recap screens. Renders the 8-cell
 * breakdown that replaced the old Funding / Interest / ROCE strip:
 *
 *   Left column                Right column
 *   -------------------------- --------------------------
 *   Prod N COS    (per product) Prod N Revenue (per product)
 *   Total COS                  Total Revenue
 *   Total 3rd Party Costs      Total On Charged Cost
 *   Net Profit                 Total Gross Profit/Margin
 *
 * Every value is computed server-side via @property's on TradeRecap
 * (`product_financials`, `total_cos`, etc.) so the FE never recomputes
 * and the math stays consistent between the recap, the delivery, and
 * the dashboard list view. Renders nothing for Solids.
 *
 * PURCHASE recaps (Chris, Sept 2026) show the left column only -- the
 * cost side -- with the 3rd party costs listed line by line and the
 * commissions left out. The list and its total come from the server
 * (`purchase_third_party_costs`); the Sales recap keeps all 8 cells.
 *
 * SALE recaps (Chris, Sept 2026) list their 3rd party costs under the total
 * too, from `sale_third_party_costs`: the purchase commission is not there
 * because the server has already put it into each product's COS. A cost the
 * server could not put into COS carries a `note` saying why.
 *
 * Props:
 *   commodityType:           'fuels' | 'solids' (required; gate)
 *   product_financials:      Array<{label, cos, revenue}> | null
 *   total_cos:               string | number | null  (DRF Decimal -> string)
 *   total_revenue:           string | number | null
 *   total_on_charged_cost:   string | number | null
 *   gross_profit_margin:     string | number | null
 *   net_profit:              string | number | null
 *   recapType:               'PURCHASE' | 'SALE' (picks the layout)
 *   purchase_third_party_costs: {lines, total, currency} | null (PURCHASE only)
 *   sale_third_party_costs:     {lines, total, currency} | null (SALE only)
 */
const formatUSD = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    const n = Number(v);
    if (Number.isNaN(n)) return '—';
    return `$${n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

const formatMoney = (v, currency) => {
    if (v === null || v === undefined || v === '') return '—';
    const n = Number(v);
    if (Number.isNaN(n)) return '—';
    if (!currency || currency === 'USD') return formatUSD(n);
    return `${currency} ${n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

const lineLabel = (line) => (line.label ? `${line.category} (${line.label})` : line.category);

/**
 * One 3rd party cost cell: a total, then each cost on its own line.
 *
 * Takes the server's `{lines, total, currency}` as-is and never picks which
 * costs belong in it -- that rule is the server's, per recap side. Exported so
 * the Sales recap layout can show its own list with the same cell. When the
 * server sent nothing the total reads "—" rather than borrowing another figure.
 */
export const ThirdPartyCostBreakdown = ({ label, breakdown }) => {
    const lines = Array.isArray(breakdown?.lines) ? breakdown.lines : [];
    const mixedCurrencies = lines.length > 0 && !breakdown?.currency;
    return (
        <div className="ffsp-cell" data-testid="ffsp-third-party">
            <span className="ffsp-label">{label}</span>
            <span className="ffsp-value">
                {mixedCurrencies ? 'Mixed currencies' : formatMoney(breakdown?.total, breakdown?.currency)}
            </span>
            {lines.length > 0 ? (
                <ul className="ffsp-lines">
                    {lines.map((line) => (
                        <li key={line.id} className="ffsp-line">
                            <span className="ffsp-line-label">
                                {lineLabel(line)}
                                {line.note && <span className="ffsp-line-note">{line.note}</span>}
                            </span>
                            <span className="ffsp-line-value">
                                {formatMoney(line.amount, line.currency)}
                            </span>
                        </li>
                    ))}
                </ul>
            ) : (
                breakdown && <span className="ffsp-line-empty">No 3rd party costs on this deal</span>
            )}
        </div>
    );
};

const FuelsFinancialSummaryPanel = ({
    recapType,
    purchase_third_party_costs,
    sale_third_party_costs,
    commodityType,
    product_financials,
    total_cos,
    total_revenue,
    total_on_charged_cost,
    gross_profit_margin,
    net_profit,
}) => {
    if (commodityType !== 'fuels') return null;

    const productRows = Array.isArray(product_financials) ? product_financials : [];
    const isPurchase = String(recapType || '').toUpperCase() === 'PURCHASE';

    // Each "row" in the screenshot pairs a left-column cell with a
    // right-column cell. Render as a 2-col CSS grid so the cells line
    // up vertically the way the PM mocked them. Negative-profit
    // values pick up the .ffsp-value--neg modifier so a loss is
    // visually obvious (red) rather than swallowed by the cell.
    const rows = [
        ...productRows.map((p) => ([
            { label: `${p.label} COS`, value: p.cos },
            { label: `${p.label} Revenue`, value: p.revenue },
        ])),
        [
            { label: 'Total COS', value: total_cos, strong: true },
            { label: 'Total Revenue', value: total_revenue, strong: true },
        ],
        [
            // The list's own total, not `total_third_party_costs`: that one
            // still includes the purchase commission already inside COS.
            { label: 'Total 3rd Party Costs', breakdown: sale_third_party_costs },
            { label: 'Total On Charged Cost', value: total_on_charged_cost },
        ],
        [
            { label: 'Net Profit', value: net_profit, strong: true },
            { label: 'Total Gross Profit / Margin', value: gross_profit_margin, strong: true },
        ],
    ];

    const renderCell = (cell, key) => {
        if (!cell) {
            return <div key={key} className="ffsp-cell ffsp-cell--empty" aria-hidden="true" />;
        }
        if ('breakdown' in cell) {
            return <ThirdPartyCostBreakdown key={key} label={cell.label} breakdown={cell.breakdown} />;
        }
        const numeric = Number(cell.value);
        const isNegative = Number.isFinite(numeric) && numeric < 0;
        return (
            <div key={key} className="ffsp-cell">
                <span className="ffsp-label">{cell.label}</span>
                <span
                    className={[
                        'ffsp-value',
                        cell.strong ? 'ffsp-value--strong' : '',
                        isNegative ? 'ffsp-value--neg' : '',
                    ].filter(Boolean).join(' ')}
                >
                    {formatUSD(cell.value)}
                </span>
            </div>
        );
    };

    if (isPurchase) {
        // Cost side only. The 3rd party list is the server's: commissions are
        // already out of it, so the lines and the total always agree.
        return (
            <div className="ffsp-grid ffsp-grid--one-col" data-testid="ffsp-purchase">
                {productRows.map((p, idx) => renderCell(
                    { label: `${p.label} COS`, value: p.cos }, `p-${idx}`,
                ))}
                {renderCell({ label: 'Total COS', value: total_cos, strong: true }, 'total-cos')}
                <ThirdPartyCostBreakdown
                    label="Total 3rd Party Costs (excl. commissions)"
                    breakdown={purchase_third_party_costs}
                />
                {renderCell({ label: 'Net Profit', value: net_profit, strong: true }, 'net-profit')}
            </div>
        );
    }

    return (
        <div className="ffsp-grid ffsp-grid--two-col">
            {rows.map((pair, rowIdx) => (
                <React.Fragment key={`row-${rowIdx}`}>
                    {renderCell(pair[0], `${rowIdx}-l`)}
                    {renderCell(pair[1], `${rowIdx}-r`)}
                </React.Fragment>
            ))}
        </div>
    );
};

export default FuelsFinancialSummaryPanel;
